import { computed, inject, onUnmounted, provide, ref, watch, type InjectionKey, type Ref, type WatchStopHandle } from 'vue';

// ============================================================================
// Types
// ============================================================================

export interface CrudBlockRegistration {
    /** Reactive flag — true when the block has unsaved changes */
    isDirty: Ref<boolean>;
    /** Persist the block's current state. Only called when isDirty is true. */
    save: () => Promise<void> | void;
    /** Revert the block to its last-saved state. */
    reset: () => void;
}

interface CrudBlockRegistryApi {
    register: (id: string, block: CrudBlockRegistration) => void;
    unregister: (id: string) => void;
}

const CRUD_BLOCK_REGISTRY_KEY: InjectionKey<CrudBlockRegistryApi> = Symbol('CrudBlockRegistry');

// ============================================================================
// Provider side — used by CrudEditor
// ============================================================================

/**
 * Creates and provides a block registry that custom blocks can register with.
 * Returns aggregated dirty state and batch save/reset helpers.
 */
export function useCrudBlockRegistry() {
    const blocks = new Map<string, CrudBlockRegistration>();
    const watchStops = new Map<string, WatchStopHandle>();
    /** Bumped whenever a block's dirty state changes — forces hasAnyDirty to re-evaluate */
    const dirtyTrigger = ref(0);

    function register(id: string, block: CrudBlockRegistration): void {
        blocks.set(id, block);
        const stop = watch(block.isDirty, () => { dirtyTrigger.value++; });
        watchStops.set(id, stop);
        dirtyTrigger.value++;
    }

    function unregister(id: string): void {
        blocks.delete(id);
        watchStops.get(id)?.();
        watchStops.delete(id);
        dirtyTrigger.value++;
    }

    provide(CRUD_BLOCK_REGISTRY_KEY, { register, unregister });

    /** True when any registered block has unsaved changes */
    const hasAnyDirty = computed(() => {
        void dirtyTrigger.value; // touch to establish reactive dependency
        for (const block of blocks.values()) {
            if (block.isDirty.value) {
                return true;
            }
        }
        return false;
    });

    /** Call save() on every dirty block. Returns when all saves settle. */
    async function saveAll(): Promise<void> {
        const promises: Promise<void>[] = [];
        for (const block of blocks.values()) {
            if (block.isDirty.value) {
                promises.push(Promise.resolve(block.save()));
            }
        }
        await Promise.all(promises);
    }

    /** Call reset() on every registered block. */
    function resetAll(): void {
        for (const block of blocks.values()) {
            block.reset();
        }
    }

    return { hasAnyDirty, saveAll, resetAll };
}

// ============================================================================
// Consumer side — used by custom blocks
// ============================================================================

/**
 * Registers a custom block with the CrudEditor's block registry.
 * Automatically unregisters on unmount. No-ops gracefully if no registry
 * is provided (e.g. block rendered outside CrudEditor).
 *
 * @param id   Unique identifier for this block (typically the block key)
 * @param reg  The block's dirty/save/reset contract
 */
export function useCrudBlockBridge(id: string, reg: CrudBlockRegistration): void {
    const registry = inject(CRUD_BLOCK_REGISTRY_KEY, null);

    if (registry) {
        registry.register(id, reg);
        onUnmounted(() => registry.unregister(id));
    }
}
